import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Send } from "lucide-react";
import { api } from "../lib/api";
import { ensurePushSubscribed, unsubscribePush, pushStatus } from "../lib/push";

/** Card compacto para ativar/desativar Web Push, reutilizável por família e
 * unidade parceira. `audience` customiza os textos ("familia" | "clinica"). */
export const PushToggle = ({ audience = "familia", testidPrefix }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushStatus().then(setStatus); }, []);
  if (!status || !status.supported) return null;

  const prefix = testidPrefix || (audience === "clinica" ? "clinic-push" : "family-push");
  const copy = audience === "clinica"
    ? {
        title: "Alertas de novas reservas",
        subOn: "Você será avisado assim que uma reserva chegar, mesmo com o painel fechado.",
        subOff: "Ative para receber alerta no navegador toda vez que um DIR chegar.",
      }
    : {
        title: "Notificações no navegador",
        subOn: "Você será alertado a cada mudança de status, mesmo com o app fechado.",
        subOff: "Ative para receber alertas em tempo real das mudanças do seu caso.",
      };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await ensurePushSubscribed();
      if (r.ok) { toast.success("Notificações ativadas"); setStatus(await pushStatus()); }
      else if (r.reason === "denied") toast.error("Permissão negada — reative em Configurações do navegador.");
    } catch (e) { toast.error(e?.message || "Falha ao ativar notificações"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await unsubscribePush(); toast.info("Notificações desativadas"); setStatus(await pushStatus()); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { await api.post("/push/test"); toast.success("Notificação de teste enviada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha no teste"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid={`${prefix}-toggle`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${status.subscribed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          {status.subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        </div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm text-[#0B2B62]">{copy.title}</div>
          <div className="text-xs text-slate-500 truncate">{status.subscribed ? copy.subOn : copy.subOff}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {status.subscribed && (
          <button onClick={sendTest} disabled={busy} data-testid={`${prefix}-test`}
            className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600 inline-flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> Testar
          </button>
        )}
        {status.subscribed ? (
          <button onClick={disable} disabled={busy} data-testid={`${prefix}-disable`}
            className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50">
            <BellOff className="w-3 h-3" /> Desativar
          </button>
        ) : (
          <button onClick={enable} disabled={busy} data-testid={`${prefix}-enable`}
            className="text-xs px-3 py-1.5 bg-[#0B2B62] text-white rounded-md hover:bg-[#0e3a82] inline-flex items-center gap-1 disabled:opacity-50">
            <Bell className="w-3 h-3" /> {busy ? "Ativando…" : "Ativar notificações"}
          </button>
        )}
      </div>
    </div>
  );
};

export default PushToggle;
